import { useMemo } from 'react'
import { graphql } from 'gatsby'

import ComparisonGrid, { TableData, TableRowData } from '@/components/ui/comparison-grid'
import RichText from '@/components/ui/rich-text'

export const query = graphql`
  fragment TableCell on ContentfulTableCell {
    id
    content {
      raw
      references {
        ...Icon
        ...Link
      }
    }
    icon {
      ...Asset
    }
  }

  fragment TableRow on ContentfulTableRow {
    id
    __typename
    rowTitle: title {
      raw
      references {
        ...Link
      }
    }
    tooltip {
      raw
      # references {
      #   ...Link
      # }
    }
    cells {
      ...TableCell
    }
  }

  fragment TableRowSectionHeading on ContentfulTableRowSectionHeading {
    id
    __typename
    sectionTitle: title
  }

  fragment ComparisonTable on ContentfulComponentComparisonTable {
    id
    internalName
    __typename
    title
    subtext {
      raw
      references {
        ...Link
      }
    }
    textAlign
    featuresHeading {
      raw
      # references {
      #   ...Link
      # }
    }
    headerCells {
      ...TableCell
    }
    rows {
      ... on ContentfulTableRow {
        ...TableRow
      }
      ... on ContentfulTableRowSectionHeading {
        ...TableRowSectionHeading
      }
    }
    footer {
      ...TableRow
    }
  }
`
type ComparisonTableProps = Queries.ComparisonTableFragment & { isPrintView?: boolean }

export default function ComparisonTable({
  internalName,
  title,
  subtext,
  featuresHeading,
  textAlign = 'Center',
  headerCells,
  rows,
  footer,
  isPrintView,
}: ComparisonTableProps) {
  const data: TableData = useMemo(
    () => ({
      id: internalName,
      tableTitle: title,
      subtext: subtext?.raw ? <RichText body={subtext as any} /> : undefined,
      headers: [
        featuresHeading?.raw ? <RichText key="features" body={featuresHeading as any} /> : undefined,
        ...(headerCells || []).map((cell) => <RichText key={cell.id} body={cell.content as any} />),
      ],
      body: (rows || []).map((row) => {
        switch (row.__typename) {
          case 'ContentfulTableRow':
            return {
              Tooltip: row.tooltip ? <RichText body={row.tooltip as any} /> : undefined,
              cells: [
                <RichText key="rowTitle" body={row.rowTitle as any} />,
                ...(row?.cells?.map((cell) => <RichText key={cell.id} body={cell.content as any} />) || []),
              ],
            }
          case 'ContentfulTableRowSectionHeading':
            return {
              subheader: row.sectionTitle,
            }
          default:
            return {}
        }
      }) as TableRowData[],
      footer: footer
        ? [
            {
              Tooltip: footer.tooltip ? <RichText body={footer.tooltip as any} /> : undefined,
              cells: [
                <RichText key="rowTitle" body={footer.rowTitle as any} />,
                ...(footer.cells?.map((cell) => <RichText key={cell.id} body={cell.content as any} />) || []),
              ],
            },
          ]
        : undefined,
    }),
    [title, featuresHeading, headerCells, rows]
  )

  return (
    <ComparisonGrid data={{ content: data }} textAlign={textAlign as 'Left' | 'Center'} isPrintView={isPrintView} />
  )
}
